<template>
  <el-drawer
    v-model="drawerVisible"
    class="approval-review-drawer"
    size="480px"
    direction="rtl"
    :append-to-body="true"
    :with-header="false"
    @open="fetchDetail"
  >
    <div v-loading="loading" class="approval-review">
      <header class="approval-review__header">
        <div>
          <div class="approval-review__eyebrow">敏感操作审核</div>
          <h3 class="approval-review__title">{{ canAuditDetail ? '待处理审核' : '审核单详情' }}</h3>
        </div>
        <div class="approval-review__header-actions">
          <el-tag v-if="detail" :type="detail.status === 'PENDING' ? 'warning' : 'info'" effect="light">
            {{ getStatusText(detail.status) }}
          </el-tag>
          <el-button :icon="Close" text circle @click="drawerVisible = false" />
        </div>
      </header>

      <el-empty v-if="!detail && !loading" description="暂无审核单详情" />
      <template v-if="detail">
        <el-alert
          v-if="detail.status !== 'PENDING'"
          class="approval-review__alert"
          type="info"
          :title="`当前状态：${getStatusText(detail.status)}`"
          show-icon
          :closable="false"
        />

        <section class="approval-review__summary">
          <div class="approval-review__operation">
            <el-tag v-for="name in detail.operationNames" :key="name" type="warning" effect="dark">
              {{ name }}
            </el-tag>
          </div>
          <div class="approval-review__business">{{ approvalBusinessName }}</div>
          <div class="approval-review__meta">
            <span>审核单号 {{ detail.ticketId }}</span>
            <span>申请人 {{ detail.applicantName || '--' }}</span>
          </div>
        </section>

        <section class="approval-review__section">
          <div class="approval-review__section-title">申请信息</div>
          <dl class="approval-review__details">
            <div v-for="row in requestDetailRows" :key="`${row.label}:${row.value}`">
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
            <div>
              <dt>申请原因</dt>
              <dd>{{ detail.applicationReason || '--' }}</dd>
            </div>
            <div>
              <dt>申请时间</dt>
              <dd>{{ detail.createTime || '--' }}</dd>
            </div>
            <div>
              <dt>过期时间</dt>
              <dd>{{ detail.expireTime || '--' }}</dd>
            </div>
          </dl>
        </section>

        <div class="approval-review__actions" :class="{ 'approval-review__actions--pending': detail.status === 'PENDING' }">
          <el-button :icon="Tickets" @click="openList">列表</el-button>
          <template v-if="canAuditDetail">
            <el-button :icon="CloseBold" :loading="submitting" @click="handleAudit('reject')">拒绝</el-button>
            <el-button :icon="Warning" type="danger" plain :loading="submitting" @click="handleAudit('void')">作废</el-button>
            <el-button :icon="Select" type="success" :loading="submitting" @click="handleAudit('approve')">通过</el-button>
          </template>
        </div>
      </template>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import type { SensitiveApprovalTicketDetail, SensitiveApprovalTicketStatus } from '@/api/sensitive-approval/types'
import { Close, CloseBold, Select, Tickets, Warning } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { getOrderDetailInfo } from '@/api/business/order'
import { getOpeningTableInfoByRecord } from '@/api/business/table'
import {
  approveSensitiveApprovalTicket,
  getSensitiveApprovalTicketDetail,
  rejectSensitiveApprovalTicket,
  voidSensitiveApprovalTicket,
} from '@/api/sensitive-approval'
import { MerchantUserSensitiveTypeDescriptions, MerchantUserSensitiveTypeEnum } from '@/constants/sensitiveType'
import { dispatchSensitiveApprovalChanged } from '@/features/sensitive-approval/approvalPending'
import { useSensitiveApprovalStore } from '@/store/modules/sensitiveApproval'
import { useUserStore } from '@/store/modules/user'
import { getAuditActionConfirmMessage, getAuditActionText, type SensitiveApprovalAuditAction, shouldPromptAuditReason } from './approvalAudit'
import { buildApprovalRequestDetailRows, formatApprovalBusinessName } from './approvalBusiness'
import { openSensitiveApprovalList } from './approvalListRoute'

const props = defineProps<{
  modelValue: boolean
  ticketId?: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  handled: []
}>()

const drawerVisible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const detail = ref<SensitiveApprovalTicketDetail>()
const loading = ref(false)
const submitting = ref(false)
const localBusinessName = ref('')
const approvalStore = useSensitiveApprovalStore()
const userStore = useUserStore()
const approvalBusinessName = computed(() => {
  if (!detail.value) {
    return '--'
  }
  const detailBusinessName = getUsableBusinessName(detail.value.businessName, detail.value.businessId)
  const storeBusinessName = getUsableBusinessName(approvalStore.ticketsById[detail.value.ticketId]?.businessName, detail.value.businessId)
  return (
    detailBusinessName ||
    localBusinessName.value ||
    storeBusinessName ||
    getRequestBodyBusinessName(detail.value) ||
    formatApprovalBusinessName({ ...detail.value, businessName: undefined }) ||
    '--'
  )
})
const requestDetailRows = computed(() => detail.value
  ? buildApprovalRequestDetailRows({
      approvalDetailRows: detail.value.approvalDetailRows,
      requestBody: detail.value.requestBody,
      requestQuery: detail.value.requestQuery,
    })
  : [],
)
const isCurrentApplicant = computed(() => {
  const ticket = detail.value
  const currentUser = userStore.getUserInfo()
  if (!ticket || !currentUser) {
    return false
  }
  const applicantCorpUserId = normalizeIdentity(ticket.applicantCorpUserId)
  const applicantMerchantUserId = normalizeIdentity(ticket.applicantMerchantUserId)
  const currentCorpUserId = normalizeIdentity(currentUser.corpUserId)
  const currentMerchantUserId = normalizeIdentity(currentUser.merchantUserId)
  return Boolean(
    (applicantCorpUserId && applicantCorpUserId === currentCorpUserId) ||
    (applicantMerchantUserId && applicantMerchantUserId === currentMerchantUserId),
  )
})
const canAuditDetail = computed(() => detail.value?.status === 'PENDING' && !isCurrentApplicant.value)

watch(() => props.ticketId, () => {
  detail.value = undefined
  if (drawerVisible.value) {
    fetchDetail()
  }
})

async function fetchDetail() {
  if (!props.ticketId) {
    return
  }
  loading.value = true
  try {
    const res = await getSensitiveApprovalTicketDetail(props.ticketId)
    detail.value = res.data
    if (isCurrentApplicant.value && detail.value?.status === 'PENDING') {
      drawerVisible.value = false
      return
    }
    await resolveLocalBusinessName()
  } finally {
    loading.value = false
  }
}

function normalizeIdentity(value?: number | string | null) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return String(value)
}

function getUsableBusinessName(businessName?: string, businessId?: string) {
  if (!businessName) {
    return ''
  }
  return String(businessName) === String(businessId) ? '' : businessName
}

function getRequestBodyBusinessName(ticket: SensitiveApprovalTicketDetail) {
  const body = getRequestBody(ticket)
  return body?.itemOrder?.itemName || body?.itemName || ''
}

function getRequestBody(ticket: SensitiveApprovalTicketDetail) {
  if (!ticket.requestBody) {
    return undefined
  }
  try {
    return JSON.parse(ticket.requestBody)
  } catch (_error) {
    return undefined
  }
}

function isOrderCancelTicket(ticket: SensitiveApprovalTicketDetail) {
  return Boolean(
    ticket.operationTypes?.includes?.(MerchantUserSensitiveTypeEnum.ORDER_CANCEL) ||
    ticket.operationNames?.includes?.(MerchantUserSensitiveTypeDescriptions[MerchantUserSensitiveTypeEnum.ORDER_CANCEL]),
  )
}

function isHangOrderTicket(ticket: SensitiveApprovalTicketDetail) {
  return Boolean(
    ticket.operationTypes?.includes?.(MerchantUserSensitiveTypeEnum.HANG_ORDER) ||
    ticket.operationNames?.includes?.(MerchantUserSensitiveTypeDescriptions[MerchantUserSensitiveTypeEnum.HANG_ORDER]),
  )
}

function isWholeOrderDiscountTicket(ticket: SensitiveApprovalTicketDetail) {
  return Boolean(
    ticket.operationTypes?.includes?.(MerchantUserSensitiveTypeEnum.DISCOUNT_TYPE_MANUAL) ||
    ticket.operationTypes?.includes?.(MerchantUserSensitiveTypeEnum.DISCOUNT_TYPE_MANUAL_DISCOUNT) ||
    ticket.operationTypes?.includes?.(MerchantUserSensitiveTypeEnum.DISCOUNT_TYPE_MANUAL_ZERO) ||
    ticket.operationNames?.includes?.(MerchantUserSensitiveTypeDescriptions[MerchantUserSensitiveTypeEnum.DISCOUNT_TYPE_MANUAL]) ||
    ticket.operationNames?.includes?.(MerchantUserSensitiveTypeDescriptions[MerchantUserSensitiveTypeEnum.DISCOUNT_TYPE_MANUAL_DISCOUNT]) ||
    ticket.operationNames?.includes?.(MerchantUserSensitiveTypeDescriptions[MerchantUserSensitiveTypeEnum.DISCOUNT_TYPE_MANUAL_ZERO]),
  )
}

function getTableRecordIdFromRequestPath(requestPath?: string) {
  const matched = requestPath?.match(/\/merchant\/table\/record\/cancel\/(\d+)/)
  return matched?.[1] ? Number(matched[1]) : undefined
}

async function fetchTableRecordName(tableRecordId?: number) {
  if (!tableRecordId) {
    return ''
  }
  try {
    const res = await getOpeningTableInfoByRecord(tableRecordId)
    return res.data?.tableName || ''
  } catch (_error) {
    return ''
  }
}

function getOrderDetailTableNames(data: Awaited<ReturnType<typeof getOrderDetailInfo>>['data']) {
  return Array.from(new Set(
    (data?.tableRecordInfo ?? [])
      .map(item => item.tableName?.trim())
      .filter(Boolean),
  )).join('、')
}

async function resolveHangOrderBusinessName(ticket: SensitiveApprovalTicketDetail) {
  const body = getRequestBody(ticket)
  const orderId = Number(body?.orderId || (ticket.businessType === 'ORDER' ? ticket.businessId : 0))
  if (orderId) {
    try {
      const res = await getOrderDetailInfo(orderId)
      const tableNames = getOrderDetailTableNames(res.data)
      if (tableNames) {
        return tableNames
      }
      const tableRecordId = Number(res.data?.orderInfo?.tableRecordId)
      const tableName = await fetchTableRecordName(tableRecordId)
      if (tableName) {
        return tableName
      }
    } catch (_error) {
      // 继续尝试按桌台记录 id 解析。
    }
  }
  return fetchTableRecordName(Number(ticket.businessId))
}

async function resolveWholeOrderDiscountBusinessName(ticket: SensitiveApprovalTicketDetail) {
  const body = getRequestBody(ticket)
  const orderId = Number(body?.orderId || (ticket.businessType === 'ORDER' ? ticket.businessId : 0))
  if (orderId) {
    try {
      const res = await getOrderDetailInfo(orderId)
      const tableNames = getOrderDetailTableNames(res.data)
      if (tableNames) {
        return tableNames
      }
    } catch (_error) {
      // 业务对象也可能是桌台记录 id，继续按桌台记录解析。
    }
  }
  return fetchTableRecordName(Number(ticket.businessId))
}

async function resolveLocalBusinessName() {
  localBusinessName.value = ''
  const ticket = detail.value
  if (!ticket || getUsableBusinessName(ticket.businessName, ticket.businessId)) {
    return
  }
  const requestBodyBusinessName = getRequestBodyBusinessName(ticket)
  if (requestBodyBusinessName) {
    localBusinessName.value = requestBodyBusinessName
    return
  }
  if (isHangOrderTicket(ticket)) {
    localBusinessName.value = await resolveHangOrderBusinessName(ticket)
    return
  }
  if (isWholeOrderDiscountTicket(ticket)) {
    localBusinessName.value = await resolveWholeOrderDiscountBusinessName(ticket)
    return
  }
  if (!isOrderCancelTicket(ticket)) {
    return
  }
  const tableRecordId = getTableRecordIdFromRequestPath(ticket.requestPath) || Number(ticket.businessId)
  if (!tableRecordId) {
    return
  }
  localBusinessName.value = await fetchTableRecordName(tableRecordId)
}

async function handleAudit(action: SensitiveApprovalAuditAction) {
  if (!detail.value) {
    return
  }
  const actionText = getAuditActionText(action)
  const confirmMessage = getAuditActionConfirmMessage(action)
  if (confirmMessage) {
    try {
      await ElMessageBox.confirm(confirmMessage, `${actionText}审核`, {
        confirmButtonText: `确定${actionText}`,
        cancelButtonText: '返回',
        type: 'warning',
      })
    } catch (_error) {
      return
    }
  }

  let reason: string | undefined
  if (shouldPromptAuditReason(action)) {
    try {
      const { value } = await ElMessageBox.prompt(`请输入${actionText}备注`, `${actionText}审核`, {
        inputType: 'textarea',
        inputPlaceholder: '可选',
        confirmButtonText: '确定',
        cancelButtonText: '取消',
      })
      reason = typeof value === 'string' ? value.trim() : undefined
    } catch (_error) {
      return
    }
  }

  submitting.value = true
  try {
    if (action === 'approve') {
      await approveSensitiveApprovalTicket(detail.value.ticketId)
    } else if (action === 'reject') {
      await rejectSensitiveApprovalTicket(detail.value.ticketId)
    } else {
      await voidSensitiveApprovalTicket(detail.value.ticketId, reason ? { reason } : undefined)
    }
    ElMessage.success(`审核已${actionText}`)
    await fetchDetail()
    if (detail.value) {
      dispatchSensitiveApprovalChanged(detail.value)
    }
    emit('handled')
  } finally {
    submitting.value = false
  }
}

function openList() {
  drawerVisible.value = false
  openSensitiveApprovalList()
}

function getStatusText(status: SensitiveApprovalTicketStatus) {
  return {
    PENDING: '待审核',
    APPROVED: '已通过',
    EXECUTING: '执行中',
    EXECUTED: '已执行',
    REJECTED: '已拒绝',
    CANCELED: '已取消',
    VOIDED: '已作废',
    EXPIRED: '已过期',
    EXECUTE_FAILED: '执行失败',
  }[status] || status
}

defineExpose({
  fetchDetail,
  handleAudit,
})
</script>

<style scoped>
.approval-review {
  position: relative;
  box-sizing: border-box;
  min-height: 100%;
  padding: 22px 22px 88px;
  background: linear-gradient(180deg, #f7f9fc 0%, #fff 180px);
}

.approval-review__header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 18px;
}

.approval-review__eyebrow {
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--el-color-primary);
}

.approval-review__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--el-text-color-primary);
}

.approval-review__header-actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  align-items: center;
}

.approval-review__alert {
  margin-bottom: 12px;
}

.approval-review__summary {
  padding: 18px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgb(31 45 61 / 8%);
}

.approval-review__operation {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.approval-review__business {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 18px;
  font-weight: 700;
  color: var(--el-text-color-primary);
  white-space: nowrap;
}

.approval-review__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-top: 10px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.approval-review__section {
  padding: 16px 18px;
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 8px;
}

.approval-review__section-title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: var(--el-text-color-primary);
}

.approval-review__details {
  margin: 0;
}

.approval-review__details div {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid var(--el-border-color-extra-light);
}

.approval-review__details div:first-child {
  border-top: 0;
}

.approval-review__details dt {
  color: var(--el-text-color-secondary);
}

.approval-review__details dd {
  min-width: 0;
  margin: 0;
  color: var(--el-text-color-primary);
  overflow-wrap: anywhere;
}

.approval-review__actions {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 14px 18px;
  background: rgb(255 255 255 / 92%);
  border-top: 1px solid var(--el-border-color-lighter);
  backdrop-filter: blur(8px);
}

.approval-review__actions--pending .el-button:last-child {
  min-width: 104px;
}
</style>

<style>
.approval-review-drawer .el-drawer__body {
  padding: 0;
}
</style>
